import { useState, useCallback } from 'react'
import { FileText, Users, TrendingUp, Receipt, Download, Package } from 'lucide-react'
import {
  getGSTR1Report, getReceivablesReport, getRevenueReport, getExpenseReport,
  exportTally, exportCsv, getMonthlyExport,
} from '../services/api'
import { formatCurrency, formatDate } from '../lib/formatters'
import StatusBadge from '../components/ui/StatusBadge'
import Avatar from '../components/ui/Avatar'
import PeriodSelector from '../components/ui/PeriodSelector'

const TABS = [
  { id: 'receivables', label: 'Receivables', icon: Users },
  { id: 'revenue', label: 'Revenue', icon: TrendingUp },
  { id: 'expenses', label: 'Expenses', icon: Receipt },
  { id: 'gstr1', label: 'GSTR-1', icon: FileText },
  { id: 'export', label: 'CA Export', icon: Package },
]

function getDefaultRange() {
  const now = new Date()
  const y = now.getFullYear()
  const m = now.getMonth()
  const from = `${y}-${String(m + 1).padStart(2, '0')}-01`
  const lastDay = new Date(y, m + 1, 0).getDate()
  const to = `${y}-${String(m + 1).padStart(2, '0')}-${lastDay}`
  return { from, to }
}

export default function Reports() {
  const [tab, setTab] = useState('receivables')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [period, setPeriod] = useState(getDefaultRange)

  const load = useCallback(async () => {
    setLoading(true)
    setData(null)
    try {
      const { from, to } = period
      let res
      if (tab === 'gstr1') res = await getGSTR1Report({ from, to })
      else if (tab === 'receivables') res = await getReceivablesReport({ from, to })
      else if (tab === 'revenue') res = await getRevenueReport({ from, to })
      else if (tab === 'expenses') res = await getExpenseReport({ from, to })
      else if (tab === 'export') {
        // Extract YYYY-MM from 'from' date for monthly export
        const month = from.slice(0, 7)
        res = await getMonthlyExport(month)
      }
      setData(res.data)
    } catch {
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [tab, period])

  const downloadFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename; a.click()
    URL.revokeObjectURL(url)
  }

  const handleExport = async (type) => {
    try {
      const { from, to } = period
      const fn = type === 'tally' ? exportTally : exportCsv
      const res = await fn({ from, to, type: 'daybook' })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      const label = `${from}_${to}`
      a.download = type === 'tally' ? `tally-export-${label}.xml` : `daybook-${label}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch { alert('Export failed') }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
        <div className="flex gap-2">
          <button onClick={() => handleExport('tally')} className="btn-secondary flex items-center gap-1.5 text-xs">
            <Download className="h-3.5 w-3.5" /> Tally XML
          </button>
          <button onClick={() => handleExport('csv')} className="btn-secondary flex items-center gap-1.5 text-xs">
            <Download className="h-3.5 w-3.5" /> Day Book CSV
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => { setTab(id); setData(null) }}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === id ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {/* Period Selector + Generate */}
      <div className="flex flex-wrap items-center gap-3">
        <PeriodSelector value={period} onChange={setPeriod} />
        <button onClick={load} className="btn-primary" disabled={loading}>
          {loading ? 'Loading...' : 'Generate'}
        </button>
      </div>

      {/* Results */}
      <div className="min-h-[200px]">
        {loading ? (
          <div className="flex items-center justify-center py-16"><div className="animate-spin h-6 w-6 border-2 border-brand-600 border-t-transparent rounded-full" /></div>
        ) : !data ? (
          <div className="card text-center py-16 text-gray-400">Click Generate to view the report.</div>
        ) : (
          <>
            {tab === 'receivables' && <ReceivablesReport data={data} />}
            {tab === 'revenue' && <RevenueReport data={data} period={period} />}
            {tab === 'expenses' && <ExpensesReport data={data} />}
            {tab === 'gstr1' && <GSTR1Report data={data} />}
            {tab === 'export' && <CAExport data={data} onDownload={downloadFile} />}
          </>
        )}
      </div>
    </div>
  )
}

/* ── Receivables Aging ──────────────────────────────────────────── */
function ReceivablesReport({ data }) {
  const buckets = [
    { key: 'current', label: 'Current (not due)', color: 'text-emerald-700 bg-emerald-50' },
    { key: 'days1_30', label: '1–30 days', color: 'text-amber-700 bg-amber-50' },
    { key: 'days31_60', label: '31–60 days', color: 'text-orange-700 bg-orange-50' },
    { key: 'days61_90', label: '61–90 days', color: 'text-red-600 bg-red-50' },
    { key: 'days90plus', label: '90+ days', color: 'text-red-800 bg-red-100' },
  ]

  const grandTotal = buckets.reduce((sum, b) => sum + (data[b.key] || []).reduce((s, inv) => s + Number(inv.balanceDue), 0), 0)

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-5 gap-3">
        {buckets.map((b) => {
          const items = data[b.key] || []
          const total = items.reduce((s, inv) => s + Number(inv.balanceDue), 0)
          return (
            <div key={b.key} className="card !p-4 text-center">
              <p className="text-xs text-gray-500 mb-1">{b.label}</p>
              <p className={`text-lg font-bold ${b.color.split(' ')[0]}`}>{formatCurrency(total)}</p>
              <p className="text-xs text-gray-400 mt-0.5">{items.length} invoice{items.length !== 1 ? 's' : ''}</p>
            </div>
          )
        })}
      </div>
      <div className="card !p-3 text-right">
        <span className="text-gray-500 mr-3">Total Outstanding:</span>
        <span className="text-xl font-bold text-gray-900">{formatCurrency(grandTotal)}</span>
      </div>

      {/* Detail table */}
      {buckets.map((b) => {
        const items = data[b.key] || []
        if (items.length === 0) return null
        return (
          <div key={b.key} className="card !p-0 overflow-hidden">
            <div className={`px-4 py-2 text-sm font-semibold ${b.color}`}>{b.label} ({items.length})</div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500 bg-gray-50/80 border-b text-xs">
                <th className="px-4 py-2 font-medium">Invoice</th>
                <th className="px-4 py-2 font-medium">Customer</th>
                <th className="px-4 py-2 font-medium">Date</th>
                <th className="px-4 py-2 font-medium">Due Date</th>
                <th className="px-4 py-2 font-medium text-right">Total</th>
                <th className="px-4 py-2 font-medium text-right">Balance Due</th>
              </tr></thead>
              <tbody>
                {items.map((inv) => (
                  <tr key={inv.id} className="border-b last:border-0">
                    <td className="px-4 py-2 font-medium text-brand-600">{inv.number || 'DRAFT'}</td>
                    <td className="px-4 py-2"><div className="flex items-center gap-2"><Avatar name={inv.customer?.name} />{inv.customer?.name}</div></td>
                    <td className="px-4 py-2 text-gray-500">{formatDate(inv.date)}</td>
                    <td className="px-4 py-2 text-gray-500">{formatDate(inv.dueDate)}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(inv.total)}</td>
                    <td className="px-4 py-2 text-right font-semibold text-red-600">{formatCurrency(inv.balanceDue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      })}
    </div>
  )
}

/* ── Revenue Report ─────────────────────────────────────────────── */
function RevenueReport({ data, period }) {
  if (!Array.isArray(data) || data.length === 0) return <div className="card text-center py-8 text-gray-400">No revenue data for this period.</div>

  const maxRevenue = Math.max(...data.map(d => d.revenue))
  const totalRevenue = data.reduce((s, d) => s + d.revenue, 0)
  const totalTax = data.reduce((s, d) => s + d.tax, 0)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="card !p-4 text-center">
          <p className="text-xs text-gray-500 mb-1">Total Revenue</p>
          <p className="text-2xl font-bold text-brand-700">{formatCurrency(totalRevenue)}</p>
        </div>
        <div className="card !p-4 text-center">
          <p className="text-xs text-gray-500 mb-1">Total Tax Collected</p>
          <p className="text-2xl font-bold text-gray-700">{formatCurrency(totalTax)}</p>
        </div>
      </div>

      {/* Bar chart */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Monthly Revenue</h3>
        <div className="space-y-2">
          {data.map((d) => (
            <div key={d.month} className="flex items-center gap-3">
              <span className="text-xs text-gray-500 w-16 flex-shrink-0">{d.month}</span>
              <div className="flex-1 h-7 bg-gray-100 rounded-full overflow-hidden relative">
                <div
                  className="h-full bg-brand-400 rounded-full transition-all duration-500"
                  style={{ width: maxRevenue > 0 ? `${(d.revenue / maxRevenue) * 100}%` : '0%' }}
                />
                <span className="absolute inset-0 flex items-center px-3 text-xs font-medium text-gray-700">
                  {formatCurrency(d.revenue)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ── Expenses Report ────────────────────────────────────────────── */
function ExpensesReport({ data }) {
  const categories = data?.byCategory || []
  const reimbursements = data?.pendingReimbursements || []
  const totalExpenses = categories.reduce((s, c) => s + c.total, 0)

  return (
    <div className="space-y-4">
      <div className="card !p-4 text-center">
        <p className="text-xs text-gray-500 mb-1">Total Expenses (Period)</p>
        <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalExpenses)}</p>
      </div>

      {/* By Category */}
      <div className="card !p-0 overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50/80"><h3 className="text-sm font-semibold text-gray-700">By Category</h3></div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-gray-500 text-xs border-b">
            <th className="px-4 py-2 font-medium">Category</th>
            <th className="px-4 py-2 font-medium text-center">Count</th>
            <th className="px-4 py-2 font-medium text-right">Total</th>
            <th className="px-4 py-2 font-medium text-right">% of Total</th>
          </tr></thead>
          <tbody>
            {categories.map((c) => (
              <tr key={c.categoryId} className="border-b last:border-0">
                <td className="px-4 py-2.5 font-medium">{c.categoryName}</td>
                <td className="px-4 py-2.5 text-center text-gray-500">{c.count}</td>
                <td className="px-4 py-2.5 text-right font-semibold">{formatCurrency(c.total)}</td>
                <td className="px-4 py-2.5 text-right text-gray-400">{totalExpenses > 0 ? `${Math.round(c.total / totalExpenses * 100)}%` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pending Reimbursements */}
      {reimbursements.length > 0 && (
        <div className="card !p-0 overflow-hidden">
          <div className="px-4 py-3 border-b bg-amber-50"><h3 className="text-sm font-semibold text-amber-800">Pending Reimbursements</h3></div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-gray-500 text-xs border-b">
              <th className="px-4 py-2 font-medium">Team Member</th>
              <th className="px-4 py-2 font-medium text-right">Amount Owed</th>
            </tr></thead>
            <tbody>
              {reimbursements.map((r) => (
                <tr key={r.teamMemberId} className="border-b last:border-0">
                  <td className="px-4 py-2.5"><div className="flex items-center gap-2"><Avatar name={r.teamMemberName} />{r.teamMemberName}</div></td>
                  <td className="px-4 py-2.5 text-right font-semibold text-amber-700">{formatCurrency(r.amountOwed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/* ── GSTR-1 Report ──────────────────────────────────────────────── */
function GSTR1Report({ data }) {
  const sections = [
    { key: 'b2b', label: 'B2B (Registered)', desc: 'Invoices to customers with GSTIN' },
    { key: 'b2cs', label: 'B2C Small', desc: 'Unregistered customers, invoice ≤ ₹2.5L' },
    { key: 'b2cl', label: 'B2C Large', desc: 'Unregistered, inter-state > ₹2.5L' },
    { key: 'exports', label: 'Exports', desc: 'Non-INR or non-India invoices' },
    { key: 'cdnr', label: 'Credit/Debit Notes', desc: 'Adjustments against registered customers' },
  ]

  return (
    <div className="space-y-4">
      <div className="card !p-3 text-sm text-gray-500">
        Period: <span className="font-medium text-gray-700">{data.period?.from}</span> to <span className="font-medium text-gray-700">{data.period?.to}</span>
      </div>

      {sections.map(({ key, label, desc }) => {
        const items = key === 'cdnr' ? (data[key]?.creditNotes || []) : (data[key]?.invoices || [])
        const count = data[key]?.count || 0
        const total = items.reduce((s, inv) => s + Number(inv.total || 0), 0)

        return (
          <div key={key} className="card !p-0 overflow-hidden">
            <div className="px-4 py-3 border-b bg-gray-50/80 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">{label}</h3>
                <p className="text-xs text-gray-400">{desc}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold">{formatCurrency(total)}</p>
                <p className="text-xs text-gray-400">{count} {count === 1 ? 'entry' : 'entries'}</p>
              </div>
            </div>
            {items.length > 0 && (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-gray-500 text-xs border-b">
                  <th className="px-4 py-2 font-medium">Number</th>
                  <th className="px-4 py-2 font-medium">Customer</th>
                  <th className="px-4 py-2 font-medium">GSTIN</th>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium text-right">Taxable</th>
                  <th className="px-4 py-2 font-medium text-right">Tax</th>
                  <th className="px-4 py-2 font-medium text-right">Total</th>
                </tr></thead>
                <tbody>
                  {items.map((inv) => (
                    <tr key={inv.id} className="border-b last:border-0">
                      <td className="px-4 py-2 font-medium text-brand-600">{inv.number || '—'}</td>
                      <td className="px-4 py-2">{inv.customer?.name || '—'}</td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-500">{inv.customer?.gstin || '—'}</td>
                      <td className="px-4 py-2 text-gray-500">{formatDate(inv.date)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(Number(inv.subtotal || 0))}</td>
                      <td className="px-4 py-2 text-right text-gray-500">{formatCurrency(Number(inv.cgst || 0) + Number(inv.sgst || 0) + Number(inv.igst || 0))}</td>
                      <td className="px-4 py-2 text-right font-semibold">{formatCurrency(inv.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )
      })}
    </div>
  )
}

/* ── CA Monthly Export ───────────────────────────────────────────── */
function CAExport({ data, onDownload }) {
  if (!data?.files) return <div className="card text-center py-8 text-gray-400">No data for this period.</div>

  const files = [
    { key: 'daybook', label: 'Day Book', desc: 'All transactions in Tally Day Book format', icon: FileText },
    { key: 'invoices', label: 'Invoices', desc: 'Invoice register with GST breakdown', icon: FileText },
    { key: 'payments', label: 'Payments', desc: 'Payment receipts log', icon: Receipt },
    { key: 'expenses', label: 'Expenses', desc: 'Approved expenses by category', icon: Receipt },
  ]

  return (
    <div className="space-y-4">
      <div className="card !p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Monthly Package for CA</h3>
        <p className="text-xs text-gray-400">Download CSV files to share with your Chartered Accountant for {data.month}.</p>
        <div className="flex gap-4 mt-3 text-xs text-gray-500">
          <span>{data.summary?.invoiceCount || 0} invoices</span>
          <span>{data.summary?.paymentCount || 0} payments</span>
          <span>{data.summary?.expenseCount || 0} expenses</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {files.map(({ key, label, desc, icon: Icon }) => {
          const file = data.files[key]
          if (!file) return null
          return (
            <div key={key} className="card flex items-center gap-4">
              <div className="p-2.5 rounded-lg bg-brand-50 text-brand-600">
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">{label}</p>
                <p className="text-xs text-gray-400">{desc}</p>
              </div>
              <button onClick={() => onDownload(file.content, file.filename)} className="btn-secondary btn-sm flex items-center gap-1">
                <Download className="h-3.5 w-3.5" /> CSV
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
